import { useState } from "react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Plus, Trash2, Copy } from "lucide-react";
import { Unit } from "@/types/types";

interface MultiUnitDetailsFormProps {
  values: {
    totalFloors?: number;
    totalUnits?: number;
    buildingFacilities?: string[];
    units?: Partial<Unit>[];
  };
  onChange: (values: {
    totalFloors?: number;
    totalUnits?: number;
    buildingFacilities?: string[];
    units?: Partial<Unit>[];
  }) => void;
}

const BUILDING_FACILITIES = [
  "Security",
  "Parking",
  "Generator",
  "Elevator",
  "Swimming Pool",
  "Gym",
  "Playground",
  "CCTV",
  "Water Supply",
  "Waste Management",
];

export default function MultiUnitDetailsForm({
  values,
  onChange,
}: MultiUnitDetailsFormProps) {
  const [bulkCreateCount, setBulkCreateCount] = useState(1);
  const [bulkTemplate, setBulkTemplate] = useState<Partial<Unit>>({
    bedrooms: 2,
    bathrooms: 1,
    area: 60,
    rent: 300000,
  });

  const toggleFacility = (facility: string) => {
    const currentFacilities = values.buildingFacilities || [];
    const newFacilities = currentFacilities.includes(facility)
      ? currentFacilities.filter((f) => f !== facility)
      : [...currentFacilities, facility];
    onChange({ ...values, buildingFacilities: newFacilities });
  };

  const addUnit = (unit: Partial<Unit>) => {
    const currentUnits = values.units || [];
    onChange({ ...values, units: [...currentUnits, unit] });
  };

  const removeUnit = (index: number) => {
    const currentUnits = values.units || [];
    onChange({ ...values, units: currentUnits.filter((_, i) => i !== index) });
  };

  const updateUnit = (index: number, updates: Partial<Unit>) => {
    const currentUnits = values.units || [];
    const newUnits = [...currentUnits];
    newUnits[index] = { ...newUnits[index], ...updates };
    onChange({ ...values, units: newUnits });
  };

  const duplicateUnit = (index: number) => {
    const currentUnits = values.units || [];
    const unitToCopy = { ...currentUnits[index] };
    onChange({ ...values, units: [...currentUnits, unitToCopy] });
  };

  const handleBulkCreate = () => {
    const currentUnits = values.units || [];
    const newUnits = Array.from({ length: bulkCreateCount }, (_, i) => ({
      id: `unit_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      name: `Unit ${currentUnits.length + i + 1}`,
      floor: 1,
      bedrooms: bulkTemplate.bedrooms || 2,
      bathrooms: bulkTemplate.bathrooms || 1,
      area: bulkTemplate.area || 60,
      rent: bulkTemplate.rent || 300000,
      status: "vacant" as const,
    }));
    onChange({ ...values, units: [...currentUnits, ...newUnits] });
    setBulkCreateCount(1);
  };

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-3xl font-semibold text-foreground">
          Building Details
        </h2>
        <p className="mt-2 text-base text-muted-foreground">
          Provide information about the building and its units
        </p>
      </div>

      {/* Building Info */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-2">
          <Label htmlFor="totalFloors" className="text-base font-medium">
            Total Floors <span className="text-red-500">*</span>
          </Label>
          <Input
            id="totalFloors"
            type="number"
            min="1"
            placeholder="e.g., 4"
            value={values.totalFloors || ""}
            onChange={(e) =>
              onChange({
                ...values,
                totalFloors: parseInt(e.target.value) || 0,
              })
            }
            className="h-12 text-base"
            required
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="totalUnits" className="text-base font-medium">
            Total Units <span className="text-red-500">*</span>
          </Label>
          <Input
            id="totalUnits"
            type="number"
            min="1"
            placeholder="e.g., 12"
            value={values.totalUnits || ""}
            onChange={(e) =>
              onChange({
                ...values,
                totalUnits: parseInt(e.target.value) || 0,
              })
            }
            className="h-12 text-base"
            required
          />
        </div>
      </div>

      {/* Building Facilities */}
      <div className="space-y-4">
        <div>
          <Label className="text-base font-medium">Building Facilities</Label>
          <p className="text-sm text-muted-foreground mt-1">
            Select facilities available in the building
          </p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
          {BUILDING_FACILITIES.map((facility) => (
            <div key={facility} className="flex items-center space-x-2">
              <Checkbox
                id={facility}
                checked={values.buildingFacilities?.includes(facility)}
                onCheckedChange={() => toggleFacility(facility)}
              />
              <label
                htmlFor={facility}
                className="text-sm font-medium leading-none cursor-pointer"
              >
                {facility}
              </label>
            </div>
          ))}
        </div>
      </div>

      {/* Bulk Create Section */}
      <div className="p-6 bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-900 rounded-xl">
        <h3 className="text-lg font-semibold text-foreground mb-4">
          Quick Add Units
        </h3>
        <p className="text-sm text-muted-foreground mb-4">
          Create multiple identical units quickly
        </p>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-4">
          <Input
            type="number"
            min="1"
            placeholder="Count"
            value={bulkCreateCount}
            onChange={(e) => setBulkCreateCount(parseInt(e.target.value) || 1)}
            className="h-10"
          />
          <Input
            type="number"
            placeholder="Bedrooms"
            value={bulkTemplate.bedrooms || ""}
            onChange={(e) =>
              setBulkTemplate({
                ...bulkTemplate,
                bedrooms: parseInt(e.target.value) || 0,
              })
            }
            className="h-10"
          />
          <Input
            type="number"
            placeholder="Bathrooms"
            value={bulkTemplate.bathrooms || ""}
            onChange={(e) =>
              setBulkTemplate({
                ...bulkTemplate,
                bathrooms: parseInt(e.target.value) || 0,
              })
            }
            className="h-10"
          />
          <Input
            type="number"
            placeholder="Area (m²)"
            value={bulkTemplate.area || ""}
            onChange={(e) =>
              setBulkTemplate({
                ...bulkTemplate,
                area: parseFloat(e.target.value) || 0,
              })
            }
            className="h-10"
          />
          <Input
            type="number"
            placeholder="Rent (₦)"
            value={bulkTemplate.rent || ""}
            onChange={(e) =>
              setBulkTemplate({
                ...bulkTemplate,
                rent: parseFloat(e.target.value) || 0,
              })
            }
            className="h-10"
          />
        </div>
        <Button onClick={handleBulkCreate} className="w-full">
          <Plus className="h-4 w-4 mr-2" />
          Create {bulkCreateCount} Unit{bulkCreateCount > 1 ? "s" : ""}
        </Button>
      </div>

      {/* Units List */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <Label className="text-base font-medium">Units</Label>
            <p className="text-sm text-muted-foreground mt-1">
              {values.units?.length || 0} unit(s) added
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() =>
              addUnit({
                id: `unit_${Date.now()}_${Math.random()
                  .toString(36)
                  .substr(2, 9)}`,
                name: `Unit ${(values.units?.length || 0) + 1}`,
                floor: 1,
                bedrooms: 2,
                bathrooms: 1,
                area: 60,
                rent: 300000,
                status: "vacant",
              })
            }
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Unit
          </Button>
        </div>

        {values.units && values.units.length > 0 ? (
          <div className="space-y-3">
            {values.units.map((unit, index) => (
              <Card key={index} className="p-4">
                <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
                  <Input
                    placeholder="Name"
                    value={unit.name || ""}
                    onChange={(e) =>
                      updateUnit(index, { name: e.target.value })
                    }
                    className="h-10"
                  />
                  <Input
                    type="number"
                    placeholder="Floor"
                    value={unit.floor || ""}
                    onChange={(e) =>
                      updateUnit(index, {
                        floor: parseInt(e.target.value) || 0,
                      })
                    }
                    className="h-10"
                  />
                  <Input
                    type="number"
                    placeholder="Beds"
                    value={unit.bedrooms || ""}
                    onChange={(e) =>
                      updateUnit(index, {
                        bedrooms: parseInt(e.target.value) || 0,
                      })
                    }
                    className="h-10"
                  />
                  <Input
                    type="number"
                    placeholder="Baths"
                    value={unit.bathrooms || ""}
                    onChange={(e) =>
                      updateUnit(index, {
                        bathrooms: parseInt(e.target.value) || 0,
                      })
                    }
                    className="h-10"
                  />
                  <Input
                    type="number"
                    placeholder="Area (m²)"
                    value={unit.area || ""}
                    onChange={(e) =>
                      updateUnit(index, {
                        area: parseFloat(e.target.value) || 0,
                      })
                    }
                    className="h-10"
                  />
                  <div className="flex gap-2">
                    <Input
                      type="number"
                      placeholder="Rent (₦)"
                      value={unit.rent || ""}
                      onChange={(e) =>
                        updateUnit(index, {
                          rent: parseFloat(e.target.value) || 0,
                        })
                      }
                      className="h-10 flex-1"
                    />
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => duplicateUnit(index)}
                      title="Duplicate"
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => removeUnit(index)}
                      title="Remove"
                    >
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-muted-foreground border-2 border-dashed border-border rounded-xl">
            No units added yet. Use bulk create or add manually.
          </div>
        )}
      </div>

      {/* Summary */}
      {values.units && values.units.length > 0 && (
        <div className="p-6 bg-muted/50 border rounded-xl">
          <h3 className="text-sm font-semibold text-muted-foreground uppercase mb-3">
            Building Summary
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <span className="text-sm text-muted-foreground">
                Total Floors
              </span>
              <p className="text-lg font-semibold text-foreground">
                {values.totalFloors || 0}
              </p>
            </div>
            <div>
              <span className="text-sm text-muted-foreground">Total Units</span>
              <p className="text-lg font-semibold text-foreground">
                {values.totalUnits || 0}
              </p>
            </div>
            <div>
              <span className="text-sm text-muted-foreground">Units Added</span>
              <p className="text-lg font-semibold text-foreground">
                {values.units.length}
              </p>
            </div>
            <div>
              <span className="text-sm text-muted-foreground">Status</span>
              <p className="text-lg font-semibold">
                {values.units.length >= (values.totalUnits || 0) ? (
                  <Badge className="bg-green-500">Complete</Badge>
                ) : (
                  <Badge variant="outline">
                    {values.totalUnits! - values.units.length} remaining
                  </Badge>
                )}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
